import React, {useEffect} from 'react';
import {Box, IconButton, Typography} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {useTranslation} from 'react-i18next';
import {SIGN_IN_COLORS, sx} from './sx';
import {RUSSIA_OUTLINE} from './russiaOutline';

export type DefenceRewardKind = 'coffin' | 'kremlin' | 'collapse' | 'bavovna' | 'sunk'

const SHOW_MS = 9000

const TEXTS: Record<DefenceRewardKind, string> = {
    coffin: 'auth.rewardCoffin',
    kremlin: 'auth.rewardKremlin',
    bavovna: 'auth.rewardBavovna',
    collapse: 'auth.rewardCollapse',
    sunk: 'auth.rewardSunk',
}

const SEA = '#1f3550'
const WAVE = '#2f5276'

// Cartoon, not a likeness: a satirical reward of the air defence game.
const Coffin = () => (
    <svg viewBox={'0 0 240 150'} width={'100%'} aria-hidden={true}>
        <ellipse cx={120} cy={138} rx={96} ry={8} fill={'#000'} opacity={0.35}/>
        {/* Open coffin seen from above, head to the left. */}
        <path d={'M 20 75 L 52 32 L 200 40 L 224 75 L 200 110 L 52 118 Z'} fill={'#5a3b2c'}/>
        <path d={'M 30 75 L 57 40 L 196 47 L 216 75 L 196 103 L 57 110 Z'} fill={'#f1e6d6'}/>
        {/* Suit and folded hands. */}
        <path d={'M 92 52 L 190 56 L 206 75 L 190 94 L 92 98 Z'} fill={'#24222b'}/>
        <path d={'M 92 66 L 140 70 L 140 80 L 92 84 Z'} fill={'#3a3742'}/>
        <ellipse cx={142} cy={75} rx={8} ry={6} fill={'#e9c9b0'}/>
        {/* Bald head with devil's horns, closed eyes under angry brows, a frown. Head to the left. */}
        <path d={'M 52 66 Q 38 60 34 46 Q 46 54 57 61 Z'} fill={'#c62828'}/>
        <path d={'M 52 84 Q 38 90 34 104 Q 46 96 57 89 Z'} fill={'#c62828'}/>
        <ellipse cx={68} cy={75} rx={23} ry={20} fill={'#ecd0b8'}/>
        <ellipse cx={76} cy={66} rx={4} ry={3} fill={'#e8a58f'} opacity={0.6}/>
        <ellipse cx={76} cy={84} rx={4} ry={3} fill={'#e8a58f'} opacity={0.6}/>
        <path d={'M 56 59 L 64 70 M 56 91 L 64 80'} stroke={'#3a2a24'} strokeWidth={3.2} strokeLinecap={'round'}/>
        <path d={'M 67 62 l 0 6 M 67 82 l 0 6'} stroke={'#5a463c'} strokeWidth={2} strokeLinecap={'round'}/>
        <circle cx={75} cy={75} r={3} fill={'#d9a98c'}/>
        <path d={'M 85 68 q -6 7 0 14'} stroke={'#5a463c'} strokeWidth={2.2} fill={'none'} strokeLinecap={'round'}/>
        {/* Two lilies and a candle. */}
        <g fill={'#fffaf0'} stroke={'#d8cbb3'}>
            <path d={'M 170 60 l 8 -10 l 4 12 l 10 -2 l -8 9 z'}/>
            <path d={'M 172 90 l 8 10 l 4 -12 l 10 2 l -8 -9 z'}/>
        </g>
        <rect x={226} y={96} width={6} height={30} fill={'#efe3c6'}/>
        <path d={'M 229 84 q -5 7 0 12 q 5 -5 0 -12 z'} fill={SIGN_IN_COLORS.blastCore}>
            <animate attributeName={'opacity'} values={'1;0.55;1'} dur={'0.8s'} repeatCount={'indefinite'}/>
        </path>
    </svg>
)

const FLAMES = [
    {x: 14, h: 30, d: 0.7}, {x: 40, h: 44, d: 0.9}, {x: 66, h: 34, d: 0.6}, {x: 92, h: 52, d: 0.85},
    {x: 120, h: 74, d: 0.8}, {x: 148, h: 48, d: 0.75}, {x: 174, h: 38, d: 0.95}, {x: 200, h: 50, d: 0.65},
    {x: 226, h: 32, d: 0.7},
]

const flame = (x: number, h: number) =>
    `M ${x - 13} 150 C ${x - 16} ${150 - h * 0.45}, ${x - 4} ${150 - h * 0.55}, ${x - 2} ${150 - h} `
    + `C ${x + 6} ${150 - h * 0.7}, ${x + 16} ${150 - h * 0.5}, ${x + 13} 150 Z`

const Kremlin = () => (
    <svg viewBox={'0 0 240 150'} width={'100%'} aria-hidden={true}>
        {/* Smoke drifting up. */}
        <g fill={'#6d6470'} opacity={0.55}>
            {[0, 1, 2].map((index) => (
                <circle key={index} cx={110 + index * 18} cy={40} r={12 + index * 3}>
                    <animate attributeName={'cy'} values={'60;0'} dur={`${3 + index}s`} repeatCount={'indefinite'}/>
                    <animate attributeName={'opacity'} values={'0.7;0'} dur={`${3 + index}s`} repeatCount={'indefinite'}/>
                </circle>
            ))}
        </g>
        {/* The wall with its swallowtail merlons. */}
        <path
            d={'M 0 150 L 0 108 ' + Array.from({length: 12}, (_, i) => {
                const x = i * 20
                return `L ${x} 100 L ${x + 6} 100 L ${x + 6} 104 L ${x + 10} 100 L ${x + 14} 104 L ${x + 14} 100 L ${x + 20} 100`
            }).join(' ') + ' L 240 108 L 240 150 Z'}
            fill={'#8e2f2a'}
        />
        {/* Spasskaya tower: body, tiers, spire, star. */}
        <rect x={98} y={74} width={44} height={76} fill={'#a3362f'}/>
        <rect x={104} y={54} width={32} height={22} fill={'#b13d34'}/>
        <circle cx={120} cy={65} r={7} fill={'#f3e9d2'} stroke={'#3a2a2a'}/>
        <path d={'M 120 65 l 0 -5 M 120 65 l 4 2'} stroke={'#3a2a2a'} strokeWidth={1.4}/>
        <rect x={108} y={38} width={24} height={18} fill={'#a3362f'}/>
        <path d={'M 106 38 L 120 6 L 134 38 Z'} fill={'#3f5c4a'}/>
        <path d={'M 120 0 l 2 4 l 4 0 l -3 3 l 1 4 l -4 -2 l -4 2 l 1 -4 l -3 -3 l 4 0 z'} fill={'#d12f2f'}/>
        <path d={'M 110 150 L 110 124 Q 120 112 130 124 L 130 150 Z'} fill={'#3a1d1b'}/>
        {/* Flames: an orange body with a yellow core, each licking at its own pace. */}
        {FLAMES.map(({x, h, d}) => (
            <g key={x} style={{transformOrigin: `${x}px 150px`, animation: `rewardFlicker ${d}s ease-in-out infinite alternate`}}>
                <path d={flame(x, h)} fill={'#e8572f'} opacity={0.92}/>
                <path d={flame(x, h * 0.6)} fill={'#f6b26b'}/>
                <path d={flame(x, h * 0.3)} fill={'#fff1c9'}/>
            </g>
        ))}
    </svg>
)

// The outline breaks into pieces that drift apart. The crack lines are hand drawn over the box.
const SHARDS = [
    {clip: 'M 0 0 L 70 0 L 62 40 L 76 70 L 58 150 L 0 150 Z', dx: -18, dy: 10, r: -9},
    {clip: 'M 70 0 L 128 0 L 118 52 L 132 90 L 112 150 L 58 150 L 76 70 L 62 40 Z', dx: -6, dy: -14, r: 6},
    {clip: 'M 128 0 L 186 0 L 176 60 L 190 150 L 112 150 L 132 90 L 118 52 Z', dx: 8, dy: 14, r: -5},
    {clip: 'M 186 0 L 240 0 L 240 150 L 190 150 L 176 60 Z', dx: 20, dy: -8, r: 10},
]

const Collapse = () => (
    <svg viewBox={'0 0 240 150'} width={'100%'} aria-hidden={true}>
        <defs>
            {SHARDS.map(({clip}, index) => (
                <clipPath key={index} id={`reward-shard-${index}`}>
                    <path d={clip}/>
                </clipPath>
            ))}
        </defs>
        {SHARDS.map(({dx, dy, r}, index) => (
            <g key={index} clipPath={`url(#reward-shard-${index})`}>
                <g style={{
                    transformOrigin: '120px 75px',
                    animation: 'rewardDrift 2.4s ease-out forwards',
                    ['--dx' as string]: `${dx}px`,
                    ['--dy' as string]: `${dy}px`,
                    ['--r' as string]: `${r}deg`,
                }}>
                    <path d={RUSSIA_OUTLINE} fill={'#6b5a70'} stroke={'#2a2030'} strokeWidth={0.8}/>
                </g>
            </g>
        ))}
    </svg>
)

// "Bavovna" (cotton): the Ukrainian joke word for the booms at Russian depots, airfields and
// refineries. Cotton bolls drop on the map and something goes off wherever one lands.
const BAVOVNA_SPOTS = [
    {x: 26, y: 104}, {x: 44, y: 88}, {x: 58, y: 110}, {x: 82, y: 96}, {x: 108, y: 104},
    {x: 136, y: 92}, {x: 164, y: 98}, {x: 190, y: 82}, {x: 214, y: 70}, {x: 70, y: 72},
]

const CottonBoll = () => (
    <g>
        <path d={'M 0 6 l -5 4 l 5 -1 l 5 1 z'} fill={'#7a5a3a'}/>
        <circle cx={-4} cy={0} r={4.2} fill={'#fbf8f1'}/>
        <circle cx={4} cy={0} r={4.2} fill={'#fbf8f1'}/>
        <circle cx={0} cy={-4} r={4.4} fill={'#ffffff'}/>
        <circle cx={0} cy={2} r={3.6} fill={'#f1ece2'}/>
    </g>
)

const Bavovna = () => (
    <svg viewBox={'0 0 240 150'} width={'100%'} aria-hidden={true}>
        <path d={RUSSIA_OUTLINE} fill={'#4a3d50'} stroke={'#2a2030'} strokeWidth={0.8}/>
        {BAVOVNA_SPOTS.map(({x, y}, index) => {
            const begin = `${(index * 0.37) % 2.4}s`
            return (
                <g key={index} transform={`translate(${x} ${y})`}>
                    <circle r={0} fill={'url(#reward-boom)'}>
                        <animate attributeName={'r'} values={'0;0;16;20'} keyTimes={'0;0.5;0.72;1'}
                                 dur={'2.4s'} begin={begin} repeatCount={'indefinite'}/>
                        <animate attributeName={'opacity'} values={'0;0;1;0'} keyTimes={'0;0.5;0.62;1'}
                                 dur={'2.4s'} begin={begin} repeatCount={'indefinite'}/>
                    </circle>
                    <g opacity={0}>
                        <animateTransform attributeName={'transform'} type={'translate'} values={'0 -60;0 0;0 0'}
                                          keyTimes={'0;0.5;1'} dur={'2.4s'} begin={begin} repeatCount={'indefinite'}/>
                        <animate attributeName={'opacity'} values={'1;1;0;0'} keyTimes={'0;0.5;0.52;1'}
                                 dur={'2.4s'} begin={begin} repeatCount={'indefinite'}/>
                        <CottonBoll/>
                    </g>
                </g>
            )
        })}
        <defs>
            <radialGradient id={'reward-boom'}>
                <stop offset={'0%'} stopColor={'#fff4d6'}/>
                <stop offset={'40%'} stopColor={SIGN_IN_COLORS.blastCore}/>
                <stop offset={'75%'} stopColor={SIGN_IN_COLORS.blastRing} stopOpacity={0.6}/>
                <stop offset={'100%'} stopColor={SIGN_IN_COLORS.blastRing} stopOpacity={0}/>
            </radialGradient>
        </defs>
    </svg>
)

const Sunk = () => (
    <svg viewBox={'0 0 240 150'} width={'100%'} aria-hidden={true}>
        <rect x={0} y={0} width={240} height={150} fill={'#162636'}/>
        {/* The outline sinks below the waterline and is gone from the map. */}
        <g style={{animation: 'rewardGoUnder 5s ease-in forwards'}}>
            <path d={RUSSIA_OUTLINE} fill={'#6b5a70'} stroke={'#2a2030'} strokeWidth={0.8}/>
        </g>
        <rect x={0} y={112} width={240} height={38} fill={SEA}/>
        <path d={'M 0 112 Q 15 106 30 112 T 60 112 T 90 112 T 120 112 T 150 112 T 180 112 T 210 112 T 240 112'}
              stroke={WAVE} strokeWidth={3} fill={'none'}/>
        {/* A dove of peace flies in once it's gone. */}
        <g style={{animation: 'rewardDove 5s ease-out forwards'}}>
            <path
                d={'M 120 40 c 10 -14 26 -16 34 -10 c -10 2 -16 8 -18 14 c 10 -2 20 2 24 10 c -12 -6 -24 -4 -32 2 '
                    + 'c -4 6 -12 8 -18 4 l -10 6 l 4 -10 c -6 -4 -4 -12 4 -14 z'}
                fill={'#f5f1e8'}
            />
            <path d={'M 150 30 l 10 -4 l -6 6 z'} fill={'#9ccc65'}/>
        </g>
    </svg>
)

const UkraineFlag = () => (
    <svg viewBox={'0 0 30 20'} width={30} height={20} aria-hidden={true}>
        <clipPath id={'reward-flag'}>
            <rect width={30} height={20} rx={3}/>
        </clipPath>
        <g clipPath={'url(#reward-flag)'}>
            <rect width={30} height={10} fill={'#0057b7'}/>
            <rect y={10} width={30} height={10} fill={'#ffd700'}/>
        </g>
    </svg>
)

const ART: Record<DefenceRewardKind, React.FC> = {
    coffin: Coffin,
    kremlin: Kremlin,
    collapse: Collapse,
    bavovna: Bavovna,
    sunk: Sunk,
}

interface IProps {
    kind: DefenceRewardKind | null,
    // Shot down drones the reward was earned with.
    count: number,
    onClose: () => void,
}

const DefenceReward = ({kind, count, onClose}: IProps) => {

    const {t} = useTranslation()

    useEffect(() => {
        if (!kind) {
            return
        }
        const timer = window.setTimeout(onClose, SHOW_MS)
        return () => window.clearTimeout(timer)
    }, [kind, onClose]);

    if (!kind) {
        return null
    }

    const Art = ART[kind]

    return (
        // Keyed by kind so a new reward starts its animations from the beginning.
        <Box key={kind} sx={sx.reward} role={'status'} aria-live={'polite'}>
            <IconButton size={'small'} onClick={onClose} sx={sx.rewardClose} aria-label={t('auth.rewardClose')}>
                <CloseIcon fontSize={'small'}/>
            </IconButton>
            <Art/>
            <Typography sx={sx.rewardTitle}>{t('auth.rewardTitle', {count})}</Typography>
            <Typography sx={sx.rewardText}>{t(TEXTS[kind])}</Typography>
            <Box sx={sx.rewardGlory}>
                <UkraineFlag/>
                <span>{t('auth.gloryToUkraine')}</span>
            </Box>
        </Box>
    );
};

export default DefenceReward;
